var Promise = require('es6-promise-promise');

module.exports = class FacebookHelper {

  constructor(appId, version) {
    if (!appId || !version) {
      console.warn(`Invalid FB AppId '${appId}' or Graph Version '${version}'`);
    }
    this.appId = appId;
    this.version = version;

    this.loadingSDK = false;

    /**
     * @var status indicating user has connected the app
     * @link https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus
     */
    this.connectedStatus = 'connected';

    /**
     * @var auth type to relogin a user to obtain additional permissions
     * @link https://developers.facebook.com/docs/facebook-login/web/permissions/#re-asking-declined-permissions
     */
    this.rerequestAuth = 'rerequest';
  }

  /**
   * Setup the Facebook SDK with configuration options.
   *
   * @return {Promise}
   */
  prepareFacebookSDK() {
    let self = this;
    return new Promise((resolve, reject) => {
      this.loadFacebookSDK().then(() => {
        FB.init({
          appId: self.appId,
          autoLogAppEvents: true,
          status: true,
          xfbml: true,
          cookie: true,
          version: self.version
        });
        resolve();
      });
    });
  };

  /**
   * Load Facebook SDK and global callback
   *
   * @return {Promise}
   */
  loadFacebookSDK() {

    let self = this;
    return new Promise((resolve, reject) => {

      self.setAsyncInit(resolve);

      // Load facebook if needed otherwise call async callback manually to resolve promise
      if (typeof(FB) === 'undefined') {
        self.loadingSDK = true;
        self.asyncAddScript();
      } else {
        self.asyncInit();
      }
    });
  };

  /**
   * Asynchronously loads the FB SDK JS Script calling window.fbAsyncInit on complete by default.
   *
   * @return {void}
   */
  asyncAddScript() {
    (function (d, s, id) {
      let js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  };

  /**
   * Sets up the global callback (window.fbAsyncInit) for FB SDK Loaded event, calling any previous callbacks set to
   * prevent race conditions.
   * @param   {resolve}  resolve  Promise resolve
   * @returns {void}
   */
  setAsyncInit(resolve) {
    let self = this;

    // handle race conditions
    const prevAsync = window.fbAsyncInit;

    self.asyncInit = window.fbAsyncInit = () => {
      window.fbAsyncInit = null;

      // handle race conditions with other inits by calling window.fbAsyncInit closure
      if (typeof(prevAsync) === 'function') {
        prevAsync();
      }

      self.loadingSDK = false;

      if (typeof(resolve) === 'function') {
        resolve();
      }
    };
  };

  /**
   * Retrieve visitor Facebook creds {accessToken, userId}
   * Will bring the application authorization screen if needed
   *
   * @param  {string} scope   Comma-separated list of permissions to request from the person using the app
   * @return {Promise}        Promise containing auth information object
   */
  getAuth(scope) {
    let self = this;
    return new Promise((resolve, reject) => {
      this.loginWithScope(scope).then(creds => {
        resolve(creds);
      }).catch(error => {
        reject(error);
      });
    });
  };

  /**
   * Retrieve visitor Facebook creds {accessToken, userId} checking the login status first
   * Will bring the application authorization screen if needed
   *
   * @param  {string} scope   Comma-separated list of permissions to request from the person using the app
   * @return {Promise}        Promise containing auth information object
   */
  getAuthWithLoginStatus(scope) {
    let self = this;
    return new Promise((resolve, reject) => {
      FB.getLoginStatus(response => {
        // the app is connected, now validate scopes
        if (response.status === self.connectedStatus) {
          this.validateResponseScope(response, scope).then(resolve).catch(reject);
        } else {
          // app not yet connected, try to login and obtain token for the scope
          this.loginWithScope(scope).then(creds => {
            resolve(creds);
          }).catch(error => {
            reject(error);
          });
        }
      });
    });
  };

  /**
   * Validate scopes that a particular token was created with. If scopes do not contain passed in scope, reauth.
   * @param   {object}  response    Facebook getLoginStatus API call response
   * @param   {string}  scope       The permission needed from FB
   * @returns {Promise}
   */
  validateResponseScope(response, scope) {
    let self = this;
    return new Promise((resolve, reject) => {
      FB.api('/me/permissions', ({data}) => {
        if (self.searchAllScopes(data, scope)) {
          const creds = self.getCredentialsFromReponse(response);
          resolve(creds);
        } else {
          // don't have scope, request it again
          self.loginWithScope(scope, self.rerequestAuth).then(creds => {
            resolve(creds);
          }).catch(error => {
            reject(error);
          });
        }
      });

    });
  };

  /**
   * Login to Facebook with a specifc scope and auth type, validating the response scope.
   * @param   {string} scope      Permission to request from FB
   * @param   {string} auth_type  Param indicating whether requesting permission AGAIN after previous failure.
   * @returns {Promise}
   */
  loginWithScope(scope, authType) {
    let self = this;
    return new Promise((resolve, reject) => {
      self.login(scope, authType).then(response => {
        const status = response.status,
          scopes = self.normalizeResponseScopes(response),
          grantedScope = self.searchAllScopes(scopes, scope);

        if (status === self.connectedStatus && grantedScope) {
          resolve(self.getCredentialsFromReponse(response));
        } else {
          reject(`Scope ${scope} was not granted. App status was ${status}.`);
        }
      });
    });
  };

  /**
   * Wrapper for promisifying the FB.login call with additional params.
   * @param   {string} scope      The permission to request from FB
   * @param   {string} auth_type  The auth type param indicating whether we're requesting permission after previous failure.
   * @returns {Promise}
   */
  login(scope, auth_type) {
    return new Promise((resolve, reject) => {
      let scopes = {scope: scope, return_scopes: true};

      if (auth_type !== undefined) {
        scopes.auth_type = auth_type;
      }

      FB.login(response => resolve(response), scopes);
    });
  };

  /**
   * Fetches the user's page access tokens given their long lived token
   * @param  {String} longLivedToken
   * @return {Promise}                
   */
  accounts(longLivedToken) {
    return new Promise((resolve, reject) => {
      FB.api(
        'me/accounts',
        {
          access_token: longLivedToken,
        },
        (response) => {
          if (response && !response.error) {
            resolve(response);
          } else {
            reject(response);
          }
        }
      );
    })
  };

  /**
   * Converts a list of scopes into an object of the form [{permission:'scope_name', 'status':'granted'}]
   * @param   {object}  response  Facebook login API call response
   * @returns {object}
   */
  normalizeResponseScopes(response) {
    const authResponse = response.authResponse || {},
      grantedScopes = authResponse.grantedScopes || '';

    return grantedScopes.length ? grantedScopes.split(',').map(scope => {
      return {
        permission: scope,
        status: 'granted'
      }
    }) : [];
  };

  /**
   * Check hash of scopes against a list of scopes expecting all to have status of 'granted'.
   * @param   {object}  scopes  Object literal of the form [{permission:'scope_name', 'status':'granted'}]
   * @param   {mixed}  scope   Permission(s) to search for
   * @returns {bool}
   */
  searchAllScopes(scopes, scope) {
    scope = scope || '';

    let self = this,
      hasAllScopes = true,
      searchScopes = scope.split(',');

    searchScopes.forEach((searchScope) => {
      if(!self.searchScopes(scopes, searchScope)) {
        hasAllScopes = false;
      }
    });
    return hasAllScopes;
  };

  /**
   * Check hash of scopes for a given granted scope.
   * @param   {object}  scopes  Object literal of the form [{permission:'scope_name', 'status':'granted'}]
   * @param   {string}  scope   Permission to search for
   * @returns {bool}
   */
  searchScopes(scopes, scope) {
    scopes = scopes || [];

    const foundScope = scopes.filter(({permission, status}) => {
      return permission === scope && status === 'granted';
    });

    return !!foundScope.length;
  };

  /**
   * Parse Facebook response and return user credentials in the form {accessToken, userId}
   *
   * @param  {object} response    Facebook login response
   * @return {object}             JavaScript object containing auth information
   */
  getCredentialsFromReponse(response) {
    if (!response.authResponse) {
      return {};
    }

    return {
      accessToken: response.authResponse.accessToken,
      userId: response.authResponse.userID
    };
  }
};
